import { useState, useCallback } from 'react';

/**
 * Custom hook for managing a Set of string values with toggle functionality
 * Useful for managing expanded states, selected items, etc.
 * 
 * @param initialSet - Optional initial Set of values
 * @returns Tuple of [set, toggle function, setState function, clear function]
 * 
 * @example
 * const [expandedIds, toggleExpanded, setExpandedIds, clearExpanded] = useToggleSet();
 * toggleExpanded('id-1'); // Adds or removes 'id-1' from set
 * setExpandedIds(new Set(['id-1', 'id-2'])); // Replace entire set
 * clearExpanded(); // Clear all values
 */
export const useToggleSet = (initialSet?: Set<string>) => {
  const [set, setSet] = useState<Set<string>>(initialSet || new Set());
  
  const toggle = useCallback((id: string) => {
    setSet(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);
  
  const clear = useCallback(() => {
    setSet(new Set());
  }, []);
  
  return [set, toggle, setSet, clear] as const;
};
